import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Popper from "@material-ui/core/Popper";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import MenuList from "@material-ui/core/MenuList";
import MenuItem from "@material-ui/core/MenuItem";
import LanguageIcon from "@material-ui/icons/Language";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import FloatingToolbarItem from "./FloatingToolbarItem";

const useDropdownStyles = makeStyles(() => ({
  dropdown: {
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    overflow: "hidden",
    marginTop: 4,
  },
  menuItem: {
    fontSize: "13px",
    padding: "8px 20px",
    color: grayColor[7],
    "&:hover": {
      backgroundColor: grayColor[8],
    },
  },
  mobileSubmenu: {
    paddingLeft: "44px",
    display: "flex",
    flexDirection: "column",
  },
  mobileSubmenuItem: {
    fontSize: "13px",
    padding: "6px 16px",
    color: grayColor[7],
    cursor: "pointer",
    "&:hover": {
      backgroundColor: grayColor[8],
    },
  },
}));

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
];

export default function LanguageToolbarItem({ _mobile, _position, ...rest }) {
  const { t, i18n } = useTranslation();
  const classes = useDropdownStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileExpanded, setMobileExpanded] = React.useState(false);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  const langCode = (i18n.language || "en").toUpperCase().slice(0, 2);
  const menuOpen = Boolean(anchorEl);

  const handleToggle = (event) => {
    setTooltipOpen(false);
    if (anchorEl && anchorEl.contains(event.target)) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSetLanguage = (lang) => (event) => {
    event.stopPropagation();
    i18n.changeLanguage(lang);
    setAnchorEl(null);
    setMobileExpanded(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (_mobile) {
    return (
      <>
        <FloatingToolbarItem
          icon={<LanguageIcon fontSize="inherit" />}
          label={langCode}
          mobileLabel={`${t("navbar.change_language")} (${langCode})`}
          onClick={() => setMobileExpanded((v) => !v)}
          _mobile
          _position={_position}
          {...rest}
        />
        {mobileExpanded && (
          <div className={classes.mobileSubmenu}>
            {LANGUAGES.map(({ code, label }) => (
              <div
                key={code}
                className={classes.mobileSubmenuItem}
                onClick={handleSetLanguage(code)}
              >
                {label}
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <FloatingToolbarItem
      icon={<LanguageIcon fontSize="inherit" />}
      label={langCode}
      tooltipTitle={t("navbar.change_language")}
      tooltipOpen={!menuOpen && tooltipOpen}
      onTooltipOpen={() => !menuOpen && setTooltipOpen(true)}
      onTooltipClose={() => setTooltipOpen(false)}
      onClick={handleToggle}
      _position={_position}
      {...rest}
    >
      <Popper
        open={menuOpen}
        anchorEl={anchorEl}
        transition
        placement="bottom"
        style={{ zIndex: 1101 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            id="language-toolbar-menu"
            style={{ transformOrigin: "center top" }}
          >
            <Paper className={classes.dropdown}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList style={{ paddingTop: 0, paddingBottom: 0 }}>
                  {LANGUAGES.map(({ code, label }) => (
                    <MenuItem
                      key={code}
                      onClick={handleSetLanguage(code)}
                      className={classes.menuItem}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </FloatingToolbarItem>
  );
}

LanguageToolbarItem.propTypes = {
  _mobile: PropTypes.bool,
  _position: PropTypes.string,
};
